import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Container, Divider, Grid, Header } from 'semantic-ui-react';
import Svg from 'react-inlinesvg';
import styled from 'styled-components';
import restFormSubmitAPI from '../api/restFormSubmitAPIs';
import { logoWarrantyImage } from '../images';
import { DataList, BottomFooter, Loader } from '../components';
import { Formatters } from '../utils';
import { colors } from '../styles';
import { withAuth } from '../auth';
import { withPageViewTracking } from '../analytics';
import { injectIntl } from 'react-intl';
import xmlToJs from 'xml-js';
import { FOOTER_FULL } from '../constants';

// TODO: fetch and replace when BE api is available
//import claim from '../data/claim.static.json';
const PaddedContainer = styled(Container)`
  padding: 2rem 0;

  @media screen and (min-width: 768px) {
    padding-bottom: 8rem;
  }
`;

const Logo = styled.header`
  padding: 2rem 0;
`;

const DisclaimerContainer = styled.div`
  padding: 1rem 0;
`;

const Disclaimer = styled.small`
  color: ${colors.lightGrey};
`;

const PrintButtonContainer = styled.div`
  overflow: hidden;
  @media print {
    display: none;
  }
`;

const PrintButton = styled(Button)`
  float: right;
  margin-bottom: 2rem;
`;

const ImageContainer = styled(Grid.Column)`
  margin-bottom: 2rem !important;
`;

const WarrantyClaimConfirmation = ({
  match: {
    params: { id }
  },
  authSession
}) => {
  const [claim, setClaim] = useState(null);
  const [imageLinks, setImageLinks] = useState(null);

  // retrieve claim info from the server and the associated images from AWS S3
  const getClaim = async () => {
    try {
      const data = await restFormSubmitAPI.getWarrantyClaim(
        id,
        authSession.accessToken
      );
      setClaim(data);
      // Next we get the images (if any) from S3
      const s3BucketURL = process.env.REACT_APP_S3_CLAIMS_BUCKET;
      const s3ImageResponse = await fetch(
        `${s3BucketURL}?list_type=2&prefix=${id}/`
      );
      const xmlText = await s3ImageResponse.text();
      const obj = JSON.parse(
        xmlToJs.xml2json(xmlText, { compact: true, spaces: 4 })
      );
      if (obj.ListBucketResult && obj.ListBucketResult.Contents) {
        let imageUrls;
        // If there are more than 1 images, S3 returns an array, but if 1 then it returns an obj
        if (obj.ListBucketResult.Contents.length > 1) {
          imageUrls = obj.ListBucketResult.Contents.map(
            img => `${s3BucketURL}/${img.Key._text}`
          );
        } else {
          imageUrls = [
            `${s3BucketURL}/${obj.ListBucketResult.Contents.Key._text}`
          ];
        }
        setImageLinks(imageUrls);
      }
    } catch (error) {
      // TODO: process error
    }
  };

  // essentially a componentDidMount() for a stateless component.
  useEffect(() => {
    getClaim();
  }, []);

  const renderClaim = () => (
    <Fragment>
      <PaddedContainer>
        <Logo>
          <Svg src={logoWarrantyImage} />
        </Logo>
        <p className="text--lead" data-qa="successfully-sbumit-warranty-claim">
          This Warranty Claim has been submitted to AGI. Your dealer will handle
          the claim details and provide you with status or request additional
          information as needed. We will wrap up your claim as quickly as we
          can.
        </p>
        <Grid stackable columns={2}>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Warranty Claim Number:',
                  data: id,
                  qa: 'warranty-claim-number'
                }
              ]}
              theme={['row']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Claim Date:',
                  data: Formatters.date(claim.dateCreated)
                }
              ]}
              theme={['row']}
            />
          </Grid.Column>
        </Grid>
        <Divider />

        <Grid stackable columns={2}>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Customer Information',
                  data: (
                    <Fragment>
                      {claim.customerName}
                      <br />
                      {claim.customerEmail}
                      <br />
                      {claim.customerPhone}
                    </Fragment>
                  ),
                  qa: 'customer-information'
                }
              ]}
              theme={['column']}
            />

            <DataList
              list={[
                {
                  title: 'Customer Address',
                  data: (
                    <Fragment>
                      {claim.customerAddress}
                      <br />
                      {claim.customerCity}, {claim.customerState}
                      <br />
                      {claim.customerZip}
                    </Fragment>
                  ),
                  qa: 'customer-address'
                }
              ]}
              theme={['column']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: claim.dealerName,
                  data: (
                    <Fragment>
                      {claim.dealerContact}
                      <br />
                      {claim.dealerEmail}
                      <br />
                      {claim.dealerPhone}
                    </Fragment>
                  ),
                  qa: 'dealer-information'
                }
              ]}
              theme={['column', 'huge']}
            />

            <DataList
              list={[
                {
                  title: 'Dealership Address',
                  data: (
                    <Fragment>
                      {claim.dealerAddress}
                      <br />
                      {claim.dealerCity}, {claim.dealerState}
                      <br />
                      {claim.dealerZip}
                    </Fragment>
                  ),
                  qa: 'dealer-address'
                }
              ]}
              theme={['column']}
            />
          </Grid.Column>
        </Grid>
        <Divider />

        <Header as="h2" data-qa="claim-model">
          {`${claim.brand.toUpperCase()} MODEL: ${claim.model}`}
        </Header>

        <Grid stackable columns={2}>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Dealer Account #',
                  data: claim.dealerNumber,
                  qa: 'dealer-account'
                }
              ]}
              theme={['column', 'tight']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Serial Number',
                  data: claim.serialNumber,
                  qa: 'serial-number'
                }
              ]}
              theme={['column', 'tight']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Date Failure Occurred',
                  data: Formatters.date(claim.dateOccurred),
                  qa: 'date-failure'
                }
              ]}
              theme={['column', 'tight']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Customer Invoice #',
                  data: claim.customerInvoiceNumber,
                  qa: 'customer-invoice-number'
                }
              ]}
              theme={['column', 'tight']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Date Purchased',
                  data: Formatters.date(claim.datePurchased),
                  qa: 'date-purchased'
                }
              ]}
              theme={['column', 'tight']}
            />
          </Grid.Column>
          <Grid.Column>
            <DataList
              list={[
                {
                  title: 'Dealer Invoice #',
                  data: claim.dealerInvoiceNumber,
                  qa: 'dealer-invoice'
                }
              ]}
              theme={['column', 'tight']}
            />
          </Grid.Column>
          {claim.length && (
            <Grid.Column>
              <DataList
                list={[
                  {
                    title: 'Length',
                    data: claim.length,
                    qa: 'length'
                  }
                ]}
                theme={['column', 'tight']}
              />
            </Grid.Column>
          )}
          {claim.estimatedUsageHours && (
            <Grid.Column>
              <DataList
                list={[
                  {
                    title: 'Estimated Usage Hours',
                    data: claim.estimatedUsageHours,
                    qa: 'estimated-hours'
                  }
                ]}
                theme={['column', 'tight']}
              />
            </Grid.Column>
          )}
        </Grid>
        <Divider />

        <Header as="h2">Description of Breakdown or Failure</Header>
        <p data-qa="description-failure" className="text--lead">
          {claim.description}
        </p>

        {imageLinks && (
          <Fragment>
            <Grid stackable columns={3} className="grid--spreaded">
              {imageLinks.map(link => (
                <ImageContainer key={link}>
                  <img src={link} alt={link} height="181px" width="373px" />
                </ImageContainer>
              ))}
            </Grid>
            <Divider />
          </Fragment>
        )}

        <DisclaimerContainer>
          <Disclaimer>
            Legal disclaimer. Issuance of a claim number does not imply
            acceptance by AGI. The claim number is for tracking purposes.
          </Disclaimer>
        </DisclaimerContainer>

        <PrintButtonContainer>
          <PrintButton onClick={() => window.print()} primary>
            Print
          </PrintButton>
        </PrintButtonContainer>
      </PaddedContainer>
      <BottomFooter footerType={FOOTER_FULL} />
    </Fragment>
  );

  const renderLoadingIcon = () => (
    <PaddedContainer>
      <Grid>
        <Grid.Column>
          <Loader active />
        </Grid.Column>
      </Grid>
    </PaddedContainer>
  );

  return <Fragment>{claim ? renderClaim() : renderLoadingIcon()}</Fragment>;
};

WarrantyClaimConfirmation.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }),
  authSession: PropTypes.object
};

export default withPageViewTracking()(
  injectIntl(withAuth(WarrantyClaimConfirmation))
);
